import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Modal from '../components/UI/Modal';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [mainProducts, setMainProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ sku: '', name: '', description: '', category: '', base_price: '', cost_price: '', tax_rate: '18', size: '', color: '', image: '', status: 'active', parent_id: '', marketplace_account_ids: [] });
  const [uploading, setUploading] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [viewMode, setViewMode] = useState('all');

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const url = `/products/list.php?page=1&limit=200${filter ? '&marketplace=' + filter : ''}`;
      const res = await api.get(url);
      if (res.data.success) setProducts(res.data.data.products || []);
      else setError(res.data.message || 'Failed to fetch');
    } catch (e) { setError('Failed to load products'); }
    finally { setLoading(false); }
  };

  const fetchMainProducts = async () => {
    try {
      const res = await api.get('/products/list.php?type=parent&limit=9999');
      if (res.data.success) setMainProducts(res.data.data.products || []);
    } catch (e) {}
  };

  useEffect(() => {
    fetchProducts();
    fetchMainProducts();
    api.get('/platforms/list.php').then(r => { if (r.data.success) setPlatforms(r.data.data || []); }).catch(() => {});
    api.get('/categories/list.php').then(r => { if (r.data.success) setCategories(r.data.data || []); }).catch(() => {});
  }, [filter]);

  const openCreate = () => {
    setEditing(null);
    setForm({ sku: '', name: '', description: '', category: '', base_price: '', cost_price: '', tax_rate: '18', size: '', color: '', image: '', status: 'active', parent_id: '', marketplace_account_ids: [] });
    setShowForm(true);
  };

  const openCreateVariant = (parent) => {
    setEditing(null);
    setForm({ sku: '', name: '', description: '', category: parent.category || '', base_price: '', cost_price: '', tax_rate: parent.tax_rate || '18', size: '', color: '', image: '', status: 'active', parent_id: String(parent.id), marketplace_account_ids: [] });
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    const platformIds = p.marketplace_account_ids ? p.marketplace_account_ids.split(',').filter(Boolean).map(Number) : [];
    setForm({ sku: p.sku || '', name: p.name || '', description: p.description || '', category: p.category || '', base_price: p.base_price || '', cost_price: p.cost_price || '', tax_rate: p.tax_rate || '18', size: p.size || '', color: p.color || '', image: p.image || '', status: p.status || 'active', parent_id: p.parent_id || '', marketplace_account_ids: platformIds });
    setShowForm(true);
  };

  const togglePlatform = (id) => {
    setForm(prev => ({
      ...prev,
      marketplace_account_ids: prev.marketplace_account_ids.includes(id)
        ? prev.marketplace_account_ids.filter(x => x !== id)
        : [...prev.marketplace_account_ids, id]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, base_price: parseFloat(form.base_price), cost_price: parseFloat(form.cost_price), tax_rate: parseFloat(form.tax_rate), parent_id: form.parent_id ? parseInt(form.parent_id) : null };
      if (editing) { await api.post('/products/update.php', { id: editing.id, ...payload }); }
      else { await api.post('/products/create.php', payload); }
      setShowForm(false);
      fetchProducts();
      fetchMainProducts();
    } catch (e) { setError('Failed to save product'); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/products/upload.php', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        setForm(prev => ({ ...prev, image: res.data.data.url }));
      } else {
        setError(res.data.message || 'Upload failed');
      }
    } catch (e) { setError('Upload failed'); }
    finally { setUploading(false); }
  };

  const removeImage = () => {
    setForm(prev => ({ ...prev, image: '' }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await api.post('/products/delete.php', { id }); fetchProducts(); fetchMainProducts(); }
    catch (e) { setError('Failed to delete product'); }
  };

  const typeColors = { amazon: 'bg-orange-100 text-orange-800', flipkart: 'bg-blue-100 text-blue-800', meesho: 'bg-purple-100 text-purple-800' };

  const groupedProducts = [];
  const variantMap = {};
  products.forEach(p => {
    if (!p.parent_id) {
      groupedProducts.push({ ...p, variants: [] });
    } else {
      if (!variantMap[p.parent_id]) variantMap[p.parent_id] = [];
      variantMap[p.parent_id].push(p);
    }
  });
  groupedProducts.forEach(g => {
    g.variants = variantMap[g.id] || [];
  });
  const orphans = products.filter(p => p.parent_id && !groupedProducts.find(g => String(g.id) === String(p.parent_id)));

  if (loading) return <div className="text-center py-20 text-gray-600">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Products</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Total: {products.length} products</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setViewMode(viewMode === 'all' ? 'grouped' : 'all')} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">
            {viewMode === 'all' ? 'Group by Main' : 'Flat View'}
          </button>
          <button onClick={openCreate} className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg">Add Product</button>
        </div>
      </div>

      <div className="flex gap-2 items-center">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Platform:</label>
        <select value={filter} onChange={e => setFilter(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
          <option value="">All Platforms</option>
          <option value="amazon">Amazon</option>
          <option value="flipkart">Flipkart</option>
          <option value="meesho">Meesho</option>
        </select>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platforms</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Color</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {products.length === 0 ? (
                <tr><td colSpan="9" className="px-6 py-12 text-center text-gray-500">No products found.</td></tr>
              ) : viewMode === 'grouped' ? (
                <>
                  {groupedProducts.map(p => (
                    <React.Fragment key={p.id}>
                      <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 bg-blue-50 dark:bg-blue-900/20">
                        <td className="px-6 py-4">
                          {p.platforms ? p.platforms.split(',').map(pt => (
                            <span key={pt.trim()} className={`inline-block px-2 py-1 text-xs font-medium rounded-full capitalize mr-1 ${typeColors[pt.trim()] || 'bg-gray-100'}`}>{pt.trim()}</span>
                          )) : <span className="text-xs text-gray-400">-</span>}
                        </td>
                        <td className="px-6 py-4 text-sm font-mono text-gray-800">{p.sku}</td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {p.name}
                          <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">Main</span>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold">₹{parseFloat(p.base_price || 0).toFixed(2)}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{p.size || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{p.color || '-'}</td>
                        <td className="px-6 py-4">{p.image ? <img src={p.image} alt="" className="w-12 h-12 object-cover rounded border" /> : '-'}</td>
                        <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-medium rounded-full ${p.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{p.status}</span></td>
                        <td className="px-6 py-4 text-sm space-x-2">
                          <button onClick={() => openEdit(p)} className="text-primary-600 hover:text-primary-900">Edit</button>
                          <button onClick={() => openCreateVariant(p)} className="text-green-600 hover:text-green-900">+Variant</button>
                          <button onClick={() => handleDelete(p.id)} className="text-red-600 hover:text-red-900">Delete</button>
                        </td>
                      </tr>
                      {p.variants.map(v => (
                        <tr key={v.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4">
                            {v.platforms ? v.platforms.split(',').map(pt => (
                              <span key={pt.trim()} className={`inline-block px-2 py-1 text-xs font-medium rounded-full capitalize mr-1 ${typeColors[pt.trim()] || 'bg-gray-100'}`}>{pt.trim()}</span>
                            )) : <span className="text-xs text-gray-400">-</span>}
                          </td>
                          <td className="px-6 py-4 text-sm font-mono text-gray-600">{v.sku}</td>
                          <td className="px-6 py-4 text-sm text-gray-700 pl-10">
                            <span className="text-gray-400 mr-1">└─</span>
                            {v.name}
                            {v.size && v.color ? (
                              <span className="ml-1 text-xs text-gray-500">({v.size}, {v.color})</span>
                            ) : v.size ? (
                              <span className="ml-1 text-xs text-gray-500">({v.size})</span>
                            ) : v.color ? (
                              <span className="ml-1 text-xs text-gray-500">({v.color})</span>
                            ) : null}
                          </td>
                          <td className="px-6 py-4 text-sm font-semibold">₹{parseFloat(v.base_price || 0).toFixed(2)}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{v.size || '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{v.color || '-'}</td>
                          <td className="px-6 py-4">{v.image ? <img src={v.image} alt="" className="w-12 h-12 object-cover rounded border" /> : '-'}</td>
                          <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-medium rounded-full ${v.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{v.status}</span></td>
                          <td className="px-6 py-4 text-sm space-x-2">
                            <button onClick={() => openEdit(v)} className="text-primary-600 hover:text-primary-900">Edit</button>
                            <button onClick={() => handleDelete(v.id)} className="text-red-600 hover:text-red-900">Delete</button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {orphans.map(v => (
                    <tr key={v.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 bg-yellow-50">
                      <td className="px-6 py-4">
                        {v.platforms ? v.platforms.split(',').map(pt => (
                          <span key={pt.trim()} className={`inline-block px-2 py-1 text-xs font-medium rounded-full capitalize mr-1 ${typeColors[pt.trim()] || 'bg-gray-100'}`}>{pt.trim()}</span>
                        )) : <span className="text-xs text-gray-400">-</span>}
                      </td>
                      <td className="px-6 py-4 text-sm font-mono text-gray-600">{v.sku}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 pl-10">
                        <span className="text-gray-400 mr-1">└─</span>
                        {v.name}
                        <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full">Orphan</span>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold">₹{parseFloat(v.base_price || 0).toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{v.size || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{v.color || '-'}</td>
                      <td className="px-6 py-4">{v.image ? <img src={v.image} alt="" className="w-12 h-12 object-cover rounded border" /> : '-'}</td>
                      <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-medium rounded-full ${v.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{v.status}</span></td>
                      <td className="px-6 py-4 text-sm space-x-2">
                        <button onClick={() => openEdit(v)} className="text-primary-600 hover:text-primary-900">Edit</button>
                        <button onClick={() => handleDelete(v.id)} className="text-red-600 hover:text-red-900">Delete</button>
                      </td>
                    </tr>
                  ))}
                </>
              ) : (
                products.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      {p.platforms ? p.platforms.split(',').map(pt => (
                        <span key={pt.trim()} className={`inline-block px-2 py-1 text-xs font-medium rounded-full capitalize mr-1 ${typeColors[pt.trim()] || 'bg-gray-100'}`}>{pt.trim()}</span>
                      )) : <span className="text-xs text-gray-400">-</span>}
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-800">{p.sku}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {p.parent_name ? <><span className="text-gray-400 text-xs">{p.parent_name} &gt;</span> {p.name}</> : p.name}
                      {!p.parent_id && <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">Main</span>}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold">₹{parseFloat(p.base_price || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{p.size || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{p.color || '-'}</td>
                    <td className="px-6 py-4">{p.image ? <img src={p.image} alt="" className="w-12 h-12 object-cover rounded border" /> : '-'}</td>
                    <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-medium rounded-full ${p.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{p.status}</span></td>
                    <td className="px-6 py-4 text-sm space-x-2">
                      <button onClick={() => openEdit(p)} className="text-primary-600 hover:text-primary-900">Edit</button>
                      {!p.parent_id && <button onClick={() => openCreateVariant(p)} className="text-green-600 hover:text-green-900">+Variant</button>}
                      <button onClick={() => handleDelete(p.id)} className="text-red-600 hover:text-red-900">Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Product' : form.parent_id ? 'Add Variant' : 'Add Product'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Product Type</label>
              <select value={form.parent_id} onChange={e => setForm({...form, parent_id: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                <option value="">Main Product</option>
                {mainProducts.filter(m => !editing || String(m.id) !== String(editing.id)).map(m => (
                  <option key={m.id} value={m.id}>Variant of: {m.name}</option>
                ))}
              </select>
            </div>
            <div><label className="block text-sm font-medium mb-1">SKU *</label><input type="text" value={form.sku} onChange={e => setForm({...form, sku: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required /></div>
            <div><label className="block text-sm font-medium mb-1">Name *</label><input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required /></div>
            <div><label className="block text-sm font-medium mb-1">Price *</label><input type="number" step="0.01" value={form.base_price} onChange={e => setForm({...form, base_price: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required /></div>
            <div><label className="block text-sm font-medium mb-1">Cost Price</label><input type="number" step="0.01" value={form.cost_price} onChange={e => setForm({...form, cost_price: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
            <div><label className="block text-sm font-medium mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                <option value="">Select</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div><label className="block text-sm font-medium mb-1">Tax Rate (%)</label><input type="number" step="0.01" value={form.tax_rate} onChange={e => setForm({...form, tax_rate: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
            <div><label className="block text-sm font-medium mb-1">Size</label><input type="text" value={form.size} onChange={e => setForm({...form, size: e.target.value})} placeholder="e.g. M, L, XL" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
            <div><label className="block text-sm font-medium mb-1">Color</label><input type="text" value={form.color} onChange={e => setForm({...form, color: e.target.value})} placeholder="e.g. Red, Blue" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Image</label>
              <div className="flex items-center gap-3">
                <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" />
                {uploading && <span className="text-sm text-gray-500">Uploading...</span>}
              </div>
              {form.image && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={form.image} alt="Preview" className="w-16 h-16 object-cover rounded border" />
                  <button type="button" onClick={removeImage} className="text-sm text-red-600 hover:text-red-900">Remove</button>
                </div>
              )}
            </div>
            <div className="md:col-span-2"><label className="block text-sm font-medium mb-1">Platforms</label>
              <div className="flex flex-wrap gap-3">
                {platforms.map(pl => (
                  <label key={pl.id} className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    <input type="checkbox" checked={form.marketplace_account_ids.includes(pl.id)} onChange={() => togglePlatform(pl.id)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                    <span className="text-sm capitalize">{pl.marketplace_type} - {pl.account_name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            <button type="submit" className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg">{editing ? 'Update' : form.parent_id ? 'Create Variant' : 'Create Product'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Products;
